/**
 * useSpeakingRooms — the client half of speaking study ROOMS (job #752).
 *
 * A ROOM IS NOT A GROUP. Groups (useStudyGroups) are for talking — yellow belt
 * and up, seven seats, text and voice notes. A room is quiet production: white
 * belt and up, adults only, six seats, and all it shows is the room's total of
 * speaking attempts and, to you, your own count. Everybody else arrives from
 * the server as present or working, with no number: this composable renders
 * what arrives and hides nothing itself.
 *
 * An attempt is one production turn in the course — the known prompt, the
 * gap, the first target voice — counted on the server from the player's own
 * events. There is no accuracy anywhere, because an attempt cannot be wrong.
 */
import { ref, inject, type Ref } from 'vue'
import { t } from './useI18n'

export interface RoomLanguage { targetLang: string; belt: string; seeds: number; courseId: string }

export interface RoomsState {
  eligible: boolean
  reason: string | null
  message: string | null
  languages: RoomLanguage[]
  room: { id: string; targetLang: string } | null
  seats: number
}

export interface SpeakingPerson {
  id: string
  firstName: string
  state: 'working' | 'here'
  mine: boolean
  /** Yours only. Nobody else's count is ever sent. */
  attempts?: number
}

export interface SpeakingState {
  roomTotal: number
  mine: { sessionId: string; attempts: number; startedAt: string } | null
  myToday: number
  people: SpeakingPerson[]
}

export interface SpeakingSummary {
  attempts: number
  startedAt: string
  endedAt: string
  /** The last LEGO played, in both languages — never a number. */
  position: { known: string; target: string } | null
  belt: string | null
}

export interface RoomView {
  room: { id: string; targetLang: string; seats: number; seated: number }
  speaking: SpeakingState
}

/**
 * The open speaking session, remembered on the device so the player's resting
 * screen can offer the way back. The server is the truth; every read of the
 * room corrects this pointer.
 */
export interface ActiveSpeaking { roomId: string; targetLang: string; startedAt: string }
const SPEAKING_KEY = 'ssi.speakingRoom'

function readActiveSpeaking(): ActiveSpeaking | null {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(SPEAKING_KEY) : null
    const v = raw ? JSON.parse(raw) : null
    return v && typeof v.roomId === 'string' ? (v as ActiveSpeaking) : null
  } catch {
    return null
  }
}

export const activeSpeaking = ref<ActiveSpeaking | null>(readActiveSpeaking())

export function setActiveSpeaking(v: ActiveSpeaking | null): void {
  activeSpeaking.value = v
  try {
    if (v) localStorage.setItem(SPEAKING_KEY, JSON.stringify(v))
    else localStorage.removeItem(SPEAKING_KEY)
  } catch {
    /* private mode: the chip simply does not show */
  }
}

/** Local midnight, so "today" in the room is the learner's own today. */
export function localMidnightIso(now = new Date()): string {
  const d = new Date(now)
  d.setHours(0, 0, 0, 0)
  return d.toISOString()
}

export function useSpeakingRooms() {
  const supabaseRef = inject<Ref<any> | null>('supabase', null)
  const state = ref<RoomsState | null>(null)
  const view = ref<RoomView | null>(null)
  const busy = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)
  /** Not in this room any more. The screen says so and goes. */
  const gone = ref(false)
  let roomReadVersion = 0

  async function authHeaders(): Promise<Record<string, string> | null> {
    try {
      const sb = supabaseRef?.value
      if (!sb) return null
      const { data } = await sb.auth.getSession()
      const token = data?.session?.access_token
      return token ? { Authorization: `Bearer ${token}` } : null
    } catch {
      return null
    }
  }

  async function call(url: string, body?: Record<string, unknown>): Promise<any | null> {
    const headers = await authHeaders()
    if (!headers) {
      error.value = t('rooms.signIn', 'Sign in to use speaking rooms.')
      return null
    }
    try {
      const resp = await fetch(url, body
        ? { method: 'POST', headers: { 'Content-Type': 'application/json', ...headers }, body: JSON.stringify(body) }
        : { headers })
      const data = await resp.json().catch(() => ({}))
      if (resp.status === 403 && data.reason === 'not_member') {
        gone.value = true
        return null
      }
      if (!resp.ok) {
        error.value = data.error || t('rooms.failed', 'That did not go through')
        return null
      }
      return data
    } catch {
      error.value = t('rooms.failed', 'That did not go through')
      return null
    }
  }

  async function refresh(): Promise<void> {
    loading.value = true
    const data = await call('/api/social/rooms')
    if (data) {
      state.value = data as RoomsState
      error.value = null
    }
    loading.value = false
  }

  async function join(lang: string, adultConfirmed: boolean): Promise<string | null> {
    busy.value = true
    const data = await call('/api/social/rooms', { action: 'join', lang, adultConfirmed })
    busy.value = false
    return (data?.room?.id as string | undefined) ?? null
  }

  async function leave(id: string): Promise<boolean> {
    busy.value = true
    const data = await call('/api/social/rooms', { action: 'leave', id })
    busy.value = false
    // A poll started before the confirmed leave cannot restore the old session.
    if (data) roomReadVersion++
    if (data && activeSpeaking.value?.roomId === id) setActiveSpeaking(null)
    return !!data
  }

  let reading = false
  /** Read the room. Polled while the screen is open; one read in flight at a time. */
  async function open(id: string): Promise<void> {
    if (reading) return
    reading = true
    const version = roomReadVersion
    if (!view.value) loading.value = true
    const data = await call(`/api/social/room?id=${encodeURIComponent(id)}&since=${encodeURIComponent(localMidnightIso())}`)
    if (data && version === roomReadVersion) {
      error.value = null
      view.value = data as RoomView
      const mine = view.value.speaking.mine
      const here = activeSpeaking.value
      // Starting before the first room read leaves the language unknown.
      if (mine && (!here || here.roomId !== id || here.targetLang !== view.value.room.targetLang)) setActiveSpeaking({ roomId: id, targetLang: view.value.room.targetLang, startedAt: mine.startedAt })
      else if (!mine && here?.roomId === id) setActiveSpeaking(null)
    }
    reading = false
    loading.value = false
  }

  async function presence(id: string, goneNow = false): Promise<void> {
    await call('/api/social/room', { id, action: 'presence', ...(goneNow ? { gone: true } : {}) })
  }

  /** Start a session. Answers with the course that counts here, or null on failure. */
  async function speakStart(id: string): Promise<{ courseId: string | null } | null> {
    busy.value = true
    const data = await call('/api/social/room', { id, action: 'speak_start' })
    busy.value = false
    if (!data?.session) return null
    // A poll begun before the confirmed start must not erase the new session.
    roomReadVersion++
    setActiveSpeaking({ roomId: id, targetLang: view.value?.room.targetLang ?? '', startedAt: data.session.startedAt })
    return { courseId: (data.courseId as string | null) ?? null }
  }

  /** End it, and hand back the summary: attempts, the last LEGO played, the belt. */
  async function speakEnd(id: string): Promise<SpeakingSummary | null> {
    busy.value = true
    const data = await call('/api/social/room', { id, action: 'speak_end' })
    busy.value = false
    // A poll begun before the confirmed end must not restore that session.
    if (data) roomReadVersion++
    if (data && activeSpeaking.value?.roomId === id) setActiveSpeaking(null)
    await open(id)
    return (data?.summary ?? null) as SpeakingSummary | null
  }

  return { state, view, busy, loading, error, gone, refresh, join, leave, open, presence, speakStart, speakEnd }
}
